import { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Loader2, KeyRound, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { apiForgot, apiReset, apiValidatePin, apiRegister, fmtErr, setStaffToken } from '@/lib/staffApi';

/* ── Login ─────────────────────────────────────────────────────── */
export function StaffLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useStaffAuth();
  // If redirected from /staff/register the email is in router state — pre-fill
  // it so a freshly-signed-up staffer can just type their password and go.
  const initialEmail = location.state?.email || '';
  const [form, setForm] = useState({ email: initialEmail, password: '' });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(form.email, form.password); navigate('/staff/home'); }
    catch (err) { toast.error(fmtErr(err, 'Could not sign in')); }
    finally { setBusy(false); }
  };
  if (forgot) return <ForgotForm onCancel={() => setForgot(false)} />;
  return (
    <Wrapper title="Sign in" sub="Flex Living Miami staff portal">
      <form onSubmit={submit} className="space-y-3" data-testid="staff-login-form">
        <Field label="Email"><Input type="email" autoFocus value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required data-testid="staff-login-email" /></Field>
        <Field label="Password"><PasswordInput value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required data-testid="staff-login-password" /></Field>
        <Button type="submit" className="w-full mt-2" disabled={busy} data-testid="staff-login-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <LogIn className="h-4 w-4 mr-1.5" />}Sign in
        </Button>
      </form>
      <div className="mt-4 flex justify-between text-xs text-muted-foreground">
        <button type="button" onClick={() => setForgot(true)} className="hover:text-primary underline-offset-2 hover:underline" data-testid="staff-forgot-link">Forgot password?</button>
        <Link to="/staff/register" className="hover:text-primary underline-offset-2 hover:underline inline-flex items-center gap-1" data-testid="staff-register-link"><UserPlus className="h-3 w-3" />Have a sign-up code?</Link>
      </div>
    </Wrapper>
  );
}

/* ── Register w/ PIN — two-step flow ─────────────────────────── */
export function StaffRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('pin');                    // 'pin' | 'details'
  const [pin, setPin] = useState('');
  const [invite, setInvite] = useState(null);                  // { name, role, email, email_locked }
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submitPin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const inv = await apiValidatePin(pin.trim());
      setInvite(inv);
      setForm(f => ({ ...f, name: inv.name || '', email: inv.email || '' }));
      setStep('details');
    } catch (err) { toast.error(fmtErr(err, 'Invalid sign-up code')); }
    finally { setBusy(false); }
  };

  const submitDetails = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      // Register, but don't keep the token — we want the staff member to sign
      // in once on this device so the muscle-memory of "open app → enter
      // password" is set up from day one.
      await apiRegister(form.name, form.email, form.password, pin.trim());
      setStaffToken(null);
      toast.success('Account created — please sign in');
      navigate('/staff/login', { replace: true, state: { email: form.email } });
    } catch (err) { toast.error(fmtErr(err, 'Could not create account')); }
    finally { setBusy(false); }
  };

  if (step === 'pin') {
    return (
      <Wrapper title="Enter your sign-up code" sub="Your admin will text or message it to you">
        <form onSubmit={submitPin} className="space-y-3" data-testid="staff-register-pin-form">
          <Field label="Sign-up code">
            <Input
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\s/g, ''))}
              required placeholder="6-digit code" inputMode="numeric" autoFocus
              className="text-center text-lg tracking-widest font-mono"
              data-testid="staff-register-pin"
            />
          </Field>
          <Button type="submit" className="w-full mt-2" disabled={busy || pin.length < 4} data-testid="staff-validate-pin-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}Continue
          </Button>
        </form>
        <p className="mt-4 text-xs text-muted-foreground text-center">Already signed up? <Link to="/staff/login" className="hover:text-primary underline">Sign in</Link></p>
      </Wrapper>
    );
  }

  return (
    <Wrapper title={`Welcome${invite?.name ? `, ${invite.name.split(' ')[0]}` : ''}`} sub="Set up your sign-in details">
      <form onSubmit={submitDetails} className="space-y-3" data-testid="staff-register-details-form">
        {invite?.role && (
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block" data-testid="invite-role-badge">
            Role: {invite.role}
          </p>
        )}
        <Field label="Full name">
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="staff-register-name" />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            required
            disabled={!!invite?.email_locked}
            data-testid="staff-register-email"
          />
        </Field>
        <Field label="Password (min 8 chars)">
          <PasswordInput minLength={8} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required data-testid="staff-register-password" defaultVisible />
        </Field>
        <Button type="submit" className="w-full mt-2" disabled={busy} data-testid="staff-register-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}Create account
        </Button>
        <button
          type="button"
          onClick={() => { setStep('pin'); setInvite(null); }}
          className="text-xs text-muted-foreground hover:text-primary w-full text-center mt-1"
          data-testid="staff-register-back"
        >
          ← Use a different code
        </button>
      </form>
    </Wrapper>
  );
}

/* ── Forgot / reset ─────────────────────────────────────────────── */
function ForgotForm({ onCancel }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await apiForgot(email); setDone(true); }
    catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <Wrapper title="Reset password" sub="Enter your email and we'll send a reset link">
      {done ? (
        <div className="text-sm text-muted-foreground text-center" data-testid="staff-forgot-success">
          If <span className="font-medium text-foreground">{email}</span> is on file, a reset link is on the way.
          <Button variant="outline" className="mt-4 w-full" onClick={onCancel}>Back to sign in</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Email"><Input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus data-testid="staff-forgot-email" /></Field>
          <Button type="submit" className="w-full" disabled={busy} data-testid="staff-forgot-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}Send reset link</Button>
          <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-primary w-full text-center mt-2">Cancel</button>
        </form>
      )}
    </Wrapper>
  );
}

export function StaffResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get('token') || '');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await apiReset(token, pw); toast.success('Password reset'); navigate('/staff/login'); }
    catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <Wrapper title="Set a new password" sub="Paste the token from your email">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Reset token"><Input value={token} onChange={e => setToken(e.target.value)} required data-testid="staff-reset-token" /></Field>
        <Field label="New password (min 8 chars)"><PasswordInput minLength={8} value={pw} onChange={e => setPw(e.target.value)} required data-testid="staff-reset-password" defaultVisible /></Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="staff-reset-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : 'Update password'}</Button>
      </form>
    </Wrapper>
  );
}

/* ── Helpers ────────────────────────────────────────────────────── */
function Wrapper({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-background to-amber-50 p-4">
      <Card className="w-full max-w-md" data-testid="staff-auth-card">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-lg bg-[#064e3b] flex items-center justify-center">
              <svg viewBox="0 0 512 512" className="h-5 w-5">
                <rect x="112" y="112" width="288" height="52" rx="14" fill="#34d399" />
                <rect x="112" y="230" width="220" height="52" rx="14" fill="#10b981" />
                <rect x="112" y="112" width="52" height="288" rx="14" fill="#6ee7b7" />
              </svg>
            </div>
            <span className="font-heading font-bold text-base">Flex Living Miami</span>
          </div>
          <CardTitle className="font-heading text-xl">{title}</CardTitle>
          <CardDescription>{sub}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
